import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { financeApi } from "@/services/finance-api";
import type {
  AcaoDoCiclo,
  AgrupamentoOsBody,
  EnviarOsBody,
  CicloBody,
  CreatePropostaBody,
  ListarPropostasParams,
  NovoPeriodoBody,
  PatchAgenciaDaPropostaBody,
  PatchFreelancerDaPropostaBody,
  PatchPropostaBody,
  PropostaDetail,
  DocumentoEditavel,
  EdicaoDeTrecho,
  SecaoDoModelo,
  TemplateResumo,
  TemplateTipo,
  VarianteDoModelo,
} from "@/types/finance";

// Keys por shape (mesma forma de parâmetros em toda a família), pra poder
// invalidar por prefixo sem colidir com um SELECT diferente na mesma chave.
const PROPOSTAS_PREFIX = ["finance", "propostas"] as const;

function templateKey(tipo: TemplateTipo, variante: VarianteDoModelo) {
  return ["finance", "templates", tipo, variante] as const;
}

function projetosElegiveisKey(agenciaId: string) {
  return ["finance", "projetos-elegiveis", agenciaId] as const;
}

function porProjetoKey(controleProjetoIds: string[]) {
  return ["finance", "propostas-por-projeto", [...controleProjetoIds].sort()] as const;
}

function propostasKey(params: ListarPropostasParams) {
  return [...PROPOSTAS_PREFIX, params] as const;
}

function propostaDetailKey(propostaId: string) {
  return ["finance", "proposta", propostaId] as const;
}

function propostaPreviewKey(propostaId: string) {
  return ["finance", "proposta-preview", propostaId] as const;
}

function documentoEmEdicaoKey(propostaId: string, documento: DocumentoEditavel) {
  return [
    ...propostaPreviewKey(propostaId),
    "edicao",
    documento.tipo,
    documento.tipo === "invite" ? documento.freelancerId : null,
  ] as const;
}

function ordensServicoKey(propostaId: string) {
  return ["finance", "proposta-ordens-servico", propostaId] as const;
}

export function useTemplate(tipo: TemplateTipo, variante: VarianteDoModelo) {
  return useQuery({
    queryKey: templateKey(tipo, variante),
    queryFn: () => financeApi.getTemplate(tipo, variante),
  });
}

export function useCatalogoDoModelo(tipo: TemplateTipo, variante: VarianteDoModelo = "corisco") {
  return useQuery({
    queryKey: [...templateKey(tipo, variante), "catalogo"],
    queryFn: () => financeApi.getTemplateCatalogo(tipo, variante),
    staleTime: 60 * 60 * 1000,
  });
}

function aplicarResumoDoTemplate(queryClient: ReturnType<typeof useQueryClient>, resumo: TemplateResumo) {
  queryClient.setQueryData(templateKey(resumo.tipo, resumo.variante), resumo);
  queryClient.invalidateQueries({ queryKey: ["finance", "proposta-preview"] });
}

export function useSalvarVersaoTemplate(tipo: TemplateTipo, variante: VarianteDoModelo) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { secoes: SecaoDoModelo[]; observacao?: string }) =>
      financeApi.salvarVersaoTemplate(tipo, variante, body),
    onSuccess: (resumo) => aplicarResumoDoTemplate(queryClient, resumo),
  });
}

export function useAtivarVersaoTemplate(tipo: TemplateTipo, variante: VarianteDoModelo) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (versaoId: string | null) => financeApi.ativarVersaoTemplate(tipo, variante, versaoId),
    onSuccess: (resumo) => aplicarResumoDoTemplate(queryClient, resumo),
  });
}

export function useProjetosElegiveis(agenciaId: string | null) {
  return useQuery({
    queryKey: projetosElegiveisKey(agenciaId ?? ""),
    queryFn: () => financeApi.listarProjetosElegiveis(agenciaId as string),
    enabled: !!agenciaId,
  });
}

/**
 * Consulta em lote de `por-projeto`, pensada para uma lista inteira (kanban de
 * vagas, grid de controle de projetos) chamar UMA vez com todos os ids
 * visíveis em vez de N chamadas por card. `enabled` deve vir condicionado à
 * tela `financeiro` (ver `useUserPermissions`), já que quem não tem a tela
 * não deve nem disparar a request.
 */
export function usePropostasPorProjeto(controleProjetoIds: string[], enabled: boolean) {
  const idsUnicos = [...new Set(controleProjetoIds)];
  return useQuery({
    queryKey: porProjetoKey(idsUnicos),
    queryFn: () => financeApi.listarPropostasPorProjeto(idsUnicos),
    enabled: enabled && idsUnicos.length > 0,
    staleTime: 60 * 1000,
  });
}

export function usePropostas(params: ListarPropostasParams) {
  return useQuery({
    queryKey: propostasKey(params),
    queryFn: () => financeApi.listarPropostas(params),
    placeholderData: (previousData) => previousData,
  });
}

export function usePropostaDetail(propostaId: string | undefined) {
  return useQuery({
    queryKey: propostaDetailKey(propostaId ?? ""),
    queryFn: () => financeApi.getProposta(propostaId as string),
    enabled: !!propostaId,
  });
}

/**
 * O documento (proposta ou invite de um freelancer) com os trechos marcados
 * para editar direto na prévia. Mesma família de chave da prévia, então tudo
 * que invalida a prévia (dados, modelo) também recarrega este documento.
 */
export function useDocumentoEmEdicao(propostaId: string | undefined, documento: DocumentoEditavel | null) {
  return useQuery({
    queryKey: documento ? documentoEmEdicaoKey(propostaId ?? "", documento) : ["finance", "sem-documento"],
    queryFn: () =>
      documento?.tipo === "invite"
        ? financeApi.getInvitePreview(propostaId as string, documento.freelancerId, true)
        : financeApi.getPropostaPreview(propostaId as string, true),
    enabled: !!propostaId && !!documento,
    staleTime: 0,
  });
}

/**
 * Grava um trecho editado no documento. Texto digitado não recarrega o
 * documento (a prévia já mostra o que a pessoa escreveu); tirar ou trazer de
 * volta um trecho muda a estrutura e recarrega.
 */
export function useEditarTrechoDoDocumento(propostaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      documento,
      secaoId,
      edicao,
    }: {
      documento: DocumentoEditavel;
      secaoId: string;
      edicao: EdicaoDeTrecho | null;
    }) =>
      edicao
        ? financeApi.salvarTrechoDoDocumento(propostaId, documento, secaoId, edicao)
        : financeApi.restaurarTrechoDoDocumento(propostaId, documento, secaoId),
    onSuccess: (_resultado, { documento, edicao }) => {
      queryClient.invalidateQueries({ queryKey: propostaDetailKey(propostaId) });
      queryClient.invalidateQueries({ queryKey: PROPOSTAS_PREFIX });
      if (!edicao || edicao.removida) {
        queryClient.invalidateQueries({ queryKey: documentoEmEdicaoKey(propostaId, documento) });
      }
    },
  });
}

export function usePropostaOrdensServico(propostaId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ordensServicoKey(propostaId ?? ""),
    queryFn: () => financeApi.getOrdensServico(propostaId as string),
    enabled: !!propostaId && enabled,
    staleTime: 0,
  });
}

export function useReagruparOrdensServico(propostaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AgrupamentoOsBody) => financeApi.reagruparOrdensServico(propostaId, body),
    onSuccess: (previa) => {
      queryClient.setQueryData(ordensServicoKey(propostaId), previa);
    },
  });
}

export function useEnviarOrdensServico(propostaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: EnviarOsBody) => financeApi.enviarOrdensServico(propostaId, body),
    onSuccess: (resposta) => {
      queryClient.setQueryData(ordensServicoKey(propostaId), resposta.previa);
      queryClient.invalidateQueries({ queryKey: propostaDetailKey(propostaId) });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ordensServicoKey(propostaId) });
      queryClient.invalidateQueries({ queryKey: propostaDetailKey(propostaId) });
    },
  });
}

export function useCriarProposta() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePropostaBody) => financeApi.criarProposta(body),
    onSuccess: (proposta) => {
      queryClient.setQueryData(propostaDetailKey(proposta.id), proposta);
      queryClient.invalidateQueries({ queryKey: PROPOSTAS_PREFIX });
      queryClient.invalidateQueries({ queryKey: ["finance", "projetos-elegiveis"] });
      queryClient.invalidateQueries({ queryKey: ["finance", "propostas-por-projeto"] });
    },
  });
}

/**
 * Grava a proposta atualizada direto no cache (`setQueryData`) em vez de só
 * invalidar: um invalidate dispararia um refetch que, chegando no meio de uma
 * digitação, sobrescreveria o campo que o usuário ainda está editando.
 */
function aplicarAtualizacaoProposta(
  queryClient: ReturnType<typeof useQueryClient>,
  proposta: PropostaDetail,
) {
  queryClient.setQueryData(propostaDetailKey(proposta.id), proposta);
  queryClient.invalidateQueries({ queryKey: PROPOSTAS_PREFIX });
  queryClient.invalidateQueries({ queryKey: propostaPreviewKey(proposta.id) });
  queryClient.invalidateQueries({ queryKey: ordensServicoKey(proposta.id) });
}

function invalidarVinculosDeProjeto(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ["finance", "projetos-elegiveis"] });
  queryClient.invalidateQueries({ queryKey: ["finance", "propostas-por-projeto"] });
}

export function useAtualizarProposta(propostaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchPropostaBody) => financeApi.atualizarProposta(propostaId, body),
    onSuccess: (proposta) => aplicarAtualizacaoProposta(queryClient, proposta),
  });
}

export function useAdicionarItens(propostaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (controleProjetoIds: string[]) =>
      financeApi.adicionarItens(propostaId, controleProjetoIds),
    onSuccess: (proposta) => {
      aplicarAtualizacaoProposta(queryClient, proposta);
      invalidarVinculosDeProjeto(queryClient);
    },
  });
}

export function useAdicionarPeriodoNovo(propostaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: NovoPeriodoBody) => financeApi.adicionarPeriodoNovo(propostaId, body),
    onSuccess: (proposta) => {
      aplicarAtualizacaoProposta(queryClient, proposta);
      invalidarVinculosDeProjeto(queryClient);
    },
  });
}

export function useRemoverItem(propostaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => financeApi.removerItem(propostaId, itemId),
    onSuccess: (proposta) => {
      aplicarAtualizacaoProposta(queryClient, proposta);
      invalidarVinculosDeProjeto(queryClient);
    },
  });
}

export function useGerarDocumento(propostaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => financeApi.gerarDocumento(propostaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: propostaDetailKey(propostaId) });
      queryClient.invalidateQueries({ queryKey: propostaPreviewKey(propostaId) });
      queryClient.invalidateQueries({ queryKey: ordensServicoKey(propostaId) });
      queryClient.invalidateQueries({ queryKey: PROPOSTAS_PREFIX });
      invalidarVinculosDeProjeto(queryClient);
    },
  });
}

export function useGerarInvite(propostaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (freelancerId: string) => financeApi.gerarInvite(propostaId, freelancerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: propostaDetailKey(propostaId) });
    },
  });
}

export function useAtualizarFreelancerDaProposta(propostaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ freelancerId, body }: { freelancerId: string; body: PatchFreelancerDaPropostaBody }) =>
      financeApi.atualizarFreelancerDaProposta(propostaId, freelancerId, body),
    onSuccess: (_documentos, { freelancerId }) => {
      queryClient.invalidateQueries({ queryKey: propostaDetailKey(propostaId) });
      queryClient.invalidateQueries({ queryKey: propostaPreviewKey(propostaId) });
      queryClient.invalidateQueries({ queryKey: ordensServicoKey(propostaId) });
      queryClient.invalidateQueries({ queryKey: ["freelancer", freelancerId] });
    },
  });
}

export function useAtualizarAgenciaDaProposta(propostaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchAgenciaDaPropostaBody) =>
      financeApi.atualizarAgenciaDaProposta(propostaId, body),
    onSuccess: (agencia) => {
      queryClient.setQueryData<PropostaDetail>(propostaDetailKey(propostaId), (atual) =>
        atual ? { ...atual, agencia } : atual,
      );
      queryClient.invalidateQueries({ queryKey: propostaDetailKey(propostaId) });
      queryClient.invalidateQueries({ queryKey: propostaPreviewKey(propostaId) });
      queryClient.invalidateQueries({ queryKey: ordensServicoKey(propostaId) });
      queryClient.invalidateQueries({ queryKey: PROPOSTAS_PREFIX });
      queryClient.invalidateQueries({ queryKey: ["agencias"] });
      queryClient.invalidateQueries({ queryKey: ["agencia", agencia.id] });
    },
  });
}

export function useMudarCicloDaProposta(propostaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ acao, body }: { acao: AcaoDoCiclo; body: CicloBody }) =>
      financeApi.mudarCicloDaProposta(propostaId, acao, body),
    onSuccess: (proposta) => {
      aplicarAtualizacaoProposta(queryClient, proposta);
      invalidarVinculosDeProjeto(queryClient);
    },
  });
}

export function useMudarCicloDoInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ inviteId, acao, body }: { inviteId: string; acao: AcaoDoCiclo; body: CicloBody }) =>
      financeApi.mudarCicloDoInvite(inviteId, acao, body),
    onSuccess: (proposta) => aplicarAtualizacaoProposta(queryClient, proposta),
  });
}

export function useCancelarProposta(propostaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (motivo: string) => financeApi.cancelarProposta(propostaId, motivo),
    onSuccess: (proposta) => {
      aplicarAtualizacaoProposta(queryClient, proposta);
      invalidarVinculosDeProjeto(queryClient);
    },
  });
}
